import { DeleteOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Table, Tooltip } from 'antd';
import { useState } from 'react';
import Navbar from '../../components/Navbar/Navbar.tsx';
import { deleteHealthmetricType } from '../../fetchHooks/deleteHealthmetricType';
import { useHealthmetrictypes } from '../../fetchHooks/useHealthmetrictypes';
import type { HealthMetricType } from '../../fetchHooks/useHealthmetrictypesWithMeasurements';
import { CreateHealthMetricModal } from './CreateHealthMetricModal.jsx';
import HealthMetricInputDebounced from './HealthMetricInputDebounced.jsx';

interface HealthmetrictypesTableProps {
  setShowEditItem: (item: HealthMetricType) => void;
  healthMetricTypes: HealthMetricType[];
  refetch: () => void;
}
export const HealthmetrictypesTable = ({
  setShowEditItem,
  healthMetricTypes,
  refetch,
}: HealthmetrictypesTableProps) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  console.log('AdminPage healthMetricTypes: ', healthMetricTypes);
  const columns = !healthMetricTypes
    ? []
    : [
        {
          title: 'Name',
          dataIndex: 'name',
          sorter: (a: HealthMetricType, b: HealthMetricType) => a.name.localeCompare(b.name),
          key: 'name',
          render: (name: string, item: HealthMetricType) => (
            <button type="button" className="link-button" onClick={() => setShowEditItem?.(item)}>
              {name}
            </button>
          ),
          responsive: ['xs', 'sm', 'lg'] as ('xs' | 'sm' | 'lg')[],
        },
        {
          title: 'Description',
          dataIndex: 'description',
          sorter: (a: HealthMetricType, b: HealthMetricType) => a.description?.localeCompare(b.description || '') || 0,
          key: 'description',
          render: (description: string, item: HealthMetricType) => (
            <button type="button" className="link-button" onClick={() => setShowEditItem?.(item)}>
              {description}
            </button>
          ),
          responsive: ['xs', 'sm', 'lg'] as ('xs' | 'sm' | 'lg')[],
        },

        {
          title: 'Action',
          key: 'action',
          render: (_: string, item: HealthMetricType) => (
            <Space.Compact size="middle">
              <Tooltip title="Delete item">
                <Popconfirm
                  title={`Are you sure to delete ${item.description}?`}
                  onConfirm={() => deleteHealthmetricType(item.id, refetch)}
                  onCancel={() => console.log('Canceled')}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button className="edit-button" shape="round" size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            </Space.Compact>
          ),
          responsive: ['xs', 'sm', 'lg'] as ('xs' | 'sm' | 'lg')[],
        },
      ];

  return (
    <>
      <Table dataSource={healthMetricTypes} columns={columns} rowKey="id" style={{ marginBottom: 16 }} />
      <Button onClick={() => setShowCreateModal(true)}>Create new</Button>
      <CreateHealthMetricModal
        show={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onOk={() => {
          setShowCreateModal(false);
          refetch?.();
        }}
      />
    </>
  );
};

const AdminPage = () => {
  const [searchInput, setSearchInput] = useState('');
  const [showEditItem, setShowEditItem] = useState<HealthMetricType | undefined>(undefined);
  const { data: healthMetricTypes, isLoading: isLoadingHealthMetrics, refetch } = useHealthmetrictypes();
  const getUpdatedItem = (id: number) => healthMetricTypes?.find((i: HealthMetricType) => i?.id === id);

  if (isLoadingHealthMetrics) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Navbar />
      <div className="app-container">
        <h1>Healthmetric types:</h1>
        Search:{' '}
        <input
          value={searchInput}
          type="text"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
        />
        <div style={{ display: 'flex' }}>
          <div style={{ margin: '2rem' }}>
            <HealthmetrictypesTable
              healthMetricTypes={healthMetricTypes?.filter?.(({ description }: HealthMetricType) =>
                description?.toLowerCase().includes(searchInput.toLowerCase()),
              )}
              refetch={refetch}
              setShowEditItem={(item: HealthMetricType) => setShowEditItem(item)}
            />
          </div>

          <div style={{ margin: '2rem' }}>
            Now editing: {showEditItem?.name}
            {showEditItem?.id && (
              <div style={{ width: '100%' }}>
                <HealthMetricInputDebounced
                  healthmetricId={showEditItem?.id}
                  label="Name"
                  propertyName="name"
                  value={getUpdatedItem(showEditItem?.id)?.name}
                  key={'name'}
                />
                <HealthMetricInputDebounced
                  healthmetricId={showEditItem?.id}
                  label="Description"
                  propertyName="description"
                  value={getUpdatedItem(showEditItem?.id)?.description}
                  key={'description'}
                />
                <HealthMetricInputDebounced
                  healthmetricId={showEditItem?.id}
                  label="Unit"
                  propertyName="unit"
                  value={getUpdatedItem(showEditItem?.id)?.unit}
                  key={'Unit'}
                />
                <HealthMetricInputDebounced
                  healthmetricId={showEditItem?.id}
                  label="Reference Low"
                  propertyName="ref_lower"
                  value={getUpdatedItem(showEditItem?.id)?.ref_lower}
                  key={'ref_lower'}
                  isNumber
                />
                <HealthMetricInputDebounced
                  healthmetricId={showEditItem?.id}
                  label="Reference High"
                  propertyName="ref_upper"
                  value={getUpdatedItem(showEditItem?.id)?.ref_upper}
                  key={'ref_upper'}
                  isNumber
                />
                <HealthMetricInputDebounced
                  healthmetricId={showEditItem?.id}
                  label="Comment"
                  propertyName="comment"
                  value={getUpdatedItem(showEditItem?.id)?.comment}
                  key={'comment'}
                  textArea
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
